// ============================================================================
// FSA TEMPLATE — DISCLOSURE DEFINITIONS (FSA-1)
// ============================================================================
// User-friendly mapping for the boolean flags that appear in
// `tbl_schedule_class.disclosure_rules` JSONB. Each flag maps to a label,
// a one-line description an accountant would recognise, and the possible
// values that the FSA-3 data-entry screen will eventually capture.
//
// Stage 1 reads disclosure_rules from the API and renders this mapping
// as a read-only "Disclosures Required" panel. When FSA-2 lands and we
// add a per-schedule additional_disclosures JSONB, we can move this
// catalog into the DB and load it via API. For now hardcoded is fine —
// the flag set is small, stable, and tied to Schedule III Division I.

export interface DisclosureDefinition {
  /** Sidebar / panel label shown to the user. */
  label: string;
  /** Short description of what the firm must disclose. */
  description: string;
  /**
   * The set of possible values the user will pick from in FSA-3.
   * Empty = the user enters free text (e.g., narrative disclosure).
   */
  options?: string[];
  /** True when free text is captured per occurrence (e.g., per security). */
  freeText?: boolean;
  /** Optional grouping hint for rendering — currently unused but reserved. */
  group?: 'security' | 'guarantee' | 'maturity' | 'compliance' | 'shareholding' | 'ageing' | 'other';
}

export const DISCLOSURE_DEFINITIONS: Record<string, DisclosureDefinition> = {
  // ----- Borrowings -----
  show_secured_unsecured_split: {
    label: 'Security',
    description: 'Disclose whether the borrowing is secured or unsecured.',
    options: ['Secured', 'Unsecured'],
    group: 'security',
  },
  show_nature_of_security: {
    label: 'Nature of Security',
    description: 'Specify the nature of security separately for each case.',
    freeText: true,
    group: 'security',
  },
  show_director_guaranteed_aggregate: {
    label: 'Guarantee',
    description: 'Disclose aggregate amount guaranteed by directors and others, separately.',
    options: ['By Directors', 'By Others'],
    group: 'guarantee',
  },
  order_bonds_by_maturity_desc: {
    label: 'Maturity Order',
    description: 'Bonds and debentures must be listed in descending order of maturity.',
    group: 'maturity',
  },
  show_continuing_default: {
    label: 'Continuing Default',
    description: 'Disclose period and amount of any continuing default in repayment of principal or interest.',
    freeText: true,
    group: 'compliance',
  },
  show_repayment_terms: {
    label: 'Repayment Terms',
    description: 'Specify repayment terms (rate of interest, original tenure, instalment schedule).',
    freeText: true,
    group: 'maturity',
  },
  redeemable_bonds_reissue_disclosure: {
    label: 'Reissue of Redeemable Bonds',
    description: 'For redeemable bonds, disclose terms of any reissue or redemption-in-progress.',
    freeText: true,
    group: 'maturity',
  },

  // ----- Trade Payables / MSME -----
  show_msme_disclosure: {
    label: 'MSME Disclosure',
    description: 'Disclose dues to Micro and Small Enterprises (principal, interest, accrued, paid).',
    freeText: true,
    group: 'compliance',
  },

  // ----- Ageing -----
  show_ageing_buckets: {
    label: 'Ageing Buckets',
    description: 'Disclose ageing in standard buckets (less than 1 year, 1–2 years, 2–3 years, more than 3 years).',
    options: ['< 1 year', '1–2 years', '2–3 years', '> 3 years'],
    group: 'ageing',
  },

  // ----- Share Capital -----
  show_promoter_holding: {
    label: 'Promoter Shareholding',
    description: 'Disclose promoter shareholding and any change during the year.',
    freeText: true,
    group: 'shareholding',
  },
  show_shareholders_above_5pct: {
    label: 'Shareholders Above 5%',
    description: 'Disclose name and holding of every shareholder holding more than 5% of shares.',
    freeText: true,
    group: 'shareholding',
  },
};

/** Resolve a disclosure flag to its definition; returns null when unknown. */
export function getDisclosureDefinition(flag: string): DisclosureDefinition | null {
  return DISCLOSURE_DEFINITIONS[flag] ?? null;
}
